/**
 * The feedback path: a design, a sentence, and the three versions that make the
 * design reproducible, posted to the edge.
 *
 * It used to be a `mailto` link, and the reason it is not is a property of that path
 * worth keeping in view. A mail body has no length a client can be trusted with, so
 * the old button attached the spec only when it fit and fell back to the clipboard
 * when it did not — which meant the reports that arrived *without* their design were
 * systematically the large ones, the sheds with the most openings and the most places
 * for a framing rule to be wrong (MILESTONES, M6.5). A POST carries the spec whole,
 * every time, and the engine's version, the spec version and the build come with it
 * without the customer being asked: the difference between "the roof looks wrong"
 * and a file that goes straight into `engine/tests/corpus`.
 *
 * It also took an email address out of the bundle. The old path had to name an inbox
 * in the client, so the inbox was public; this one names a same-origin endpoint, and
 * there is no address anywhere in the app — not ours, and never the customer's: the
 * form asks for none, and the edge removes any typed into the note (DESIGN §9).
 */

import {
  MAX_NOTE_CHARS,
  type ReportContext,
  type ReportRequest,
  type ReportResponse,
} from "@planforge/api/wire";
import { SPEC_VERSION, loadEngine, type Spec } from "@planforge/wasm";

import { ask } from "../purchase/api.js";

export { MAX_NOTE_CHARS };
export type { ReportContext, ReportResponse };

/** What the report says when the engine never loaded — which may be the bug. */
export const ENGINE_NOT_LOADED = "not-loaded";

/** How long to wait on the engine for its version before reporting without it. */
const ENGINE_WAIT_MS = 2000;

/**
 * The app build this bundle is: its package version and the commit it was built
 * from, injected by Vite.
 *
 * Read through `typeof` because the test runner does not run Vite's `define`, and a
 * report from a test is a report from no build in particular.
 */
export function buildId(): string {
  return typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "unbuilt";
}

/**
 * The engine's semver, if it loaded.
 *
 * The report form is reachable from the crash screen, where the engine may be the
 * thing that failed — so this asks the same cached promise everything else does, and
 * gives up after two seconds rather than hold a bug report hostage to the bug.
 */
export async function engineVersion(): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<string>((resolve) => {
    timer = setTimeout(() => resolve(ENGINE_NOT_LOADED), ENGINE_WAIT_MS);
  });
  try {
    return await Promise.race([loadEngine().then((engine) => engine.version), timeout]);
  } catch {
    return ENGINE_NOT_LOADED;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The request, as data. Pure, so a test can hold the whole of what is sent.
 *
 * The note is trimmed and nothing else: what the customer wrote is evidence, and the
 * edge is the one place that decides what of it is not kept.
 */
export function reportRequest(options: {
  spec: Spec;
  note: string;
  context: ReportContext;
  engineVersion: string;
  buildId: string;
}): ReportRequest {
  return {
    spec: options.spec,
    note: options.note.trim().slice(0, MAX_NOTE_CHARS),
    context: options.context,
    engineVersion: options.engineVersion,
    specVersion: SPEC_VERSION,
    buildId: options.buildId,
  };
}

/** Posts a report. Throws an `ApiFailure` on a refusal or when nothing answered. */
export async function sendReport(request: ReportRequest): Promise<ReportResponse> {
  const response = await ask("/api/report", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });
  return (await response.json()) as ReportResponse;
}

/** What the form says when the report could not be sent. The note stays in the box. */
export const REPORT_UNREACHABLE =
  "The report couldn't be sent just now — our side isn't answering. Your note is " +
  "still here; try again in a minute.";

/** For the clipboard, which is the fallback when the edge cannot be reached. */
export function specJson(spec: Spec): string {
  return JSON.stringify(spec, null, 2);
}

/** Best effort: an older browser or a denied permission is not worth an error. */
export async function copySpec(spec: Spec): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(specJson(spec));
    return true;
  } catch {
    return false;
  }
}
